import type { ContextFile } from "@scoach/types";
import { Check, Close, Doc, Link, Plus, Trash } from "@scoach/ui/icons";
import { useState } from "react";

import { isDriveUrl, runDriveImport } from "../../../../lib/driveImport.ts";
import { preMeetingApi } from "../../../premeeting/api.ts";

export interface LiveContextPanelProps {
  meetingId: string;
  initialFiles: ContextFile[];
}

/**
 * Live-meeting "Context docs" card (left rail, next to Notes). Lets the rep attach
 * a document mid-call — by upload OR a Google Drive/Docs link — to sharpen the
 * hints. Both paths hit the same `/meetings/:id/context*` routes, which refresh the
 * running session so new docs improve hints within ~one cycle (no reload).
 */
export function LiveContextPanel({ meetingId, initialFiles }: LiveContextPanelProps) {
  const [files, setFiles] = useState<ContextFile[]>(initialFiles);
  const [url, setUrl] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onUpload(list: FileList | null) {
    if (!list || list.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await preMeetingApi.uploadContext(meetingId, Array.from(list));
      setFiles((prev) => [...prev, ...r.files]);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onImportLink() {
    const link = url.trim();
    if (!link) return;
    if (!isDriveUrl(link)) {
      setErr("Enter a Google Docs/Drive link");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await runDriveImport((token) =>
        preMeetingApi.importContextLink(meetingId, link, token),
      );
      setFiles((prev) => [...prev, ...r.files]);
      setUrl("");
      setShowLink(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(fileId: string) {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
    try {
      await preMeetingApi.removeContext(meetingId, fileId);
    } catch {
      /* best-effort; server is source of truth on next load */
    }
  }

  return (
    <section className="card notes-card">
      <div className="card-head">
        <div className="card-title">
          <Doc size={16} /> Context docs
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <label
            className="ghost-btn xs"
            style={{ cursor: "pointer" }}
            title="Upload a file from this computer"
          >
            <Plus size={13} /> Upload file
            <input
              type="file"
              multiple
              hidden
              disabled={busy}
              onChange={(e) => {
                void onUpload(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
          <button
            type="button"
            className="ghost-btn xs"
            disabled={busy}
            title="Paste a link to a file in your Google Drive"
            onClick={() => setShowLink((v) => !v)}
          >
            <Link size={13} /> Add Drive link
          </button>
        </div>
      </div>

      {showLink && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 4 }}>
            Paste a <strong>Google Drive / Docs share link</strong> (make sure it's shared so SellSynq
            can read it):
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="input sm"
              style={{ flex: 1, minWidth: 0 }}
              placeholder="https://docs.google.com/…  or  https://drive.google.com/…"
              value={url}
              disabled={busy}
              autoFocus
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void onImportLink();
                } else if (e.key === "Escape") {
                  setShowLink(false);
                }
              }}
            />
            <button
              type="button"
              className="pill-btn sm"
              disabled={busy || !url.trim()}
              onClick={() => void onImportLink()}
            >
              {busy ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      )}

      {err && (
        <div className="notes-empty" style={{ color: "var(--danger, #c00)" }}>
          {err}
        </div>
      )}

      {files.length === 0 ? (
        <div className="notes-empty">
          Attach battlecards, notes, or a customer doc — SellSynq reads them so hints reference the
          content. Upload a file or import a Google Drive link.
        </div>
      ) : (
        <ul className="notes-list">
          {files.map((f) => (
            <li key={f.id} className="notes-item">
              <span
                className="notes-text"
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                {f.indexed ? (
                  <Check size={13} title="Read — feeding hints" />
                ) : (
                  <Close size={13} title="No readable text extracted" />
                )}
                {f.sourceUrl ? (
                  <a href={f.sourceUrl} target="_blank" rel="noopener noreferrer">
                    {f.name}
                  </a>
                ) : (
                  f.name
                )}
              </span>
              <div className="notes-row-actions">
                <button
                  type="button"
                  className="ghost-btn xs danger"
                  onClick={() => void onRemove(f.id)}
                >
                  <Trash size={13} /> Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
